import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useHydrated } from '../hooks/useHydrated';
import { resolvedComponents } from '../hydrationPreload';
import ProductCard from './ItemCard/ProductCard';
import StatsWrapper from '../utils/StatsWrapper';
import { toast } from 'react-hot-toast';
import { formatItem, processSearchResultItemData } from '../utils/itemUtils';
import NewAmazonCard from './ItemCard/NewAmazonCard';
import japaneseFlag from '../assets/japanese-flag.png';
import { applySorting } from '../utils/sortingUtils';
import { convertCurrency, getCurrencySymbol } from '../services/currencyService';
import { hasActiveFilters, getActiveFilterCount } from '../utils/filterUtils';
import { Filter } from 'lucide-react';
import { AMAZON_DOMAINS } from '../utils/amazonUtils';

/**
 * Deterministic replacement for the old module-level Math.random() ad/invite-card
 * positions. Those were picked once per browser tab and never revisited - harmless for
 * the live SPA, but under SSR hydration (docs/seo-ssr-search-pages-design.md §6 hazard
 * #5) a module-level random value is frozen for the whole server process while the
 * client would independently re-roll its own, visibly swapping the card slot the
 * instant hydration completes. Seeding from searchTerm makes server and client agree.
 */
export function hashStringToInt(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (Math.imul(hash, 31) + str.charCodeAt(i)) >>> 0;
  }
  return hash;
}

function SearchResults({
  items,
  featureFlags,
  searchTerm,
  urlAsin,
  sortOption = 'amazon_results',
  selectedCurrency = 'local',
  currencyRates = {},
  activeFilters = {},
  originalItemsCount = 0,
}) {
  const hydrated = useHydrated();
  // Resolved by preloadHydratedChunks() before `hydrated` can be true - see
  // hydrationPreload.js for why these are no longer lazy()+Suspense.
  const VatInfoModal = resolvedComponents.VatInfoModal;
  const TrackItemModal = resolvedComponents.TrackItemModal;
  const ItemFullscreenModal = resolvedComponents.ItemFullscreenModal;
  const MissingDomainsBanner = resolvedComponents.MissingDomainsBanner;
  const AmazonCommunityBanner = resolvedComponents.AmazonCommunityBanner;
  const AliExpressAdCard = resolvedComponents.AliExpressAdCard;
  const adCardSeed = useMemo(() => hashStringToInt(searchTerm || ''), [searchTerm]);
  const newAmazonInviteIndex = (adCardSeed % 4) + 3; // 3-6
  const aliExpressAdIndex = (Math.floor(adCardSeed / 4) % 5) + 1; // 1-5

  const [itemsState, setItemsState] = useState(items);
  const [aliExpressProduct, setAliExpressProduct] = useState(null);
  const [historicItems, setHistoricItems] = useState([]);
  const [historicSearched, setHistoricSearched] = useState(false);
  // openTrackModal events are captured here, in the always-mounted SearchResults,
  // because TrackItemModal doesn't mount until hydration completes and its chunk is
  // resolved - a listener inside the modal would miss any event fired before that.
  const [trackModalRequest, setTrackModalRequest] = useState(null);
  // openItemFullscreen events are captured here, in the always-mounted SearchResults,
  // instead of by ~30 individual per-card listeners (one ItemFullscreenModal per
  // product) - see ItemFullscreenModal.jsx for why that was the main contributor to
  // the hydration-dispatcher-corruption bug. The matching item's full data is looked
  // up from itemsState below, right before rendering.
  // `token` increments on every open event (even reopening the same ASIN) so the
  // `key` passed to ItemFullscreenModal always changes and forces a remount - without
  // it, reopening the same product during its close animation would set the exact
  // same asin string again, React would bail out of the state update (Object.is
  // equality), and the reopen click would silently no-op.
  const [fullscreenRequest, setFullscreenRequest] = useState(null);
  // Mounting VatInfoModal directly off `hydrated` put it in the exact same commit as
  // every other hydrated-gated component (banners, ad cards, InstructionsModal, etc.) -
  // useHydrated's markHydrated() notifies all of its subscribers synchronously in one
  // pass, and that mass simultaneous first-mount is what corrupted React's hooks
  // dispatcher in production (React #321/#418/#423, confirmed live via console errors
  // on /search/{slug} pages). Mirrors the same fix in Hero.jsx for InstructionsModal:
  // `showVatInfo` flips one animation frame after `hydrated`, in its own effect, not
  // inside markHydrated()'s notify loop.
  const [showVatInfo, setShowVatInfo] = useState(false);

  useEffect(() => {
    if (!hydrated) return;
    const rafId = requestAnimationFrame(() => setShowVatInfo(true));
    return () => cancelAnimationFrame(rafId);
  }, [hydrated]);

  useEffect(() => {
    const handleOpenTrackModal = (event) => setTrackModalRequest({ detail: event.detail });
    document.addEventListener('openTrackModal', handleOpenTrackModal);
    return () => document.removeEventListener('openTrackModal', handleOpenTrackModal);
  }, []);

  useEffect(() => {
    const handleOpenFullscreen = (event) => {
      const asin = event.detail?.asin ?? null;
      setFullscreenRequest(asin ? (prev) => ({ asin, token: (prev?.token ?? 0) + 1 }) : null);
    };
    document.addEventListener('openItemFullscreen', handleOpenFullscreen);
    return () => document.removeEventListener('openItemFullscreen', handleOpenFullscreen);
  }, []);

  const fullscreenItem = fullscreenRequest
    ? itemsState?.find((item) => item.id === fullscreenRequest.asin)
    : null;

  useEffect(() => {
    // Reset when search term changes
    setAliExpressProduct(null);
    setHistoricItems([]);
    setHistoricSearched(false);

    if (!searchTerm) return;

    const fetchAliExpressProduct = async () => {
      try {
        const response = await fetch(`/api/search-aliexpress?q=${encodeURIComponent(searchTerm)}`);
        if (response.ok) {
          const data = await response.json();
          setAliExpressProduct(data);
        }
      } catch (error) {
        console.error('Failed to fetch AliExpress alternative:', error);
      }
    };

    fetchAliExpressProduct();
  }, [searchTerm]);

  /**
   * Get missing domains from search results
   * @param {Array} items - Array of search result items
   * @returns {Array<string>} Array of missing domain names
   */
  const getMissingDomains = (items) => {
    if (!items || !Array.isArray(items)) {
      return Object.keys(AMAZON_DOMAINS);
    }

    // Get all domains present in the results
    const presentDomains = new Set();
    items.forEach((item) => {
      if (item.locales && Array.isArray(item.locales)) {
        item.locales.forEach((locale) => {
          if (locale.domain) {
            presentDomains.add(locale.domain);
          }
        });
      }
    });

    // Find missing domains
    const missingDomains = Object.keys(AMAZON_DOMAINS).filter(
      (domain) => !presentDomains.has(domain)
    );
    return missingDomains;
  };

  // Currency conversion function
  const convertLocalesPrices = (locales, targetCurrency) => {
    if (targetCurrency === 'local' || !currencyRates) {
      return locales; // Return original prices if local currency or no rates
    }

    return locales.map((locale) => {
      const originalCurrency = locale.currency;
      const originalPrice = locale.price;
      const originalPriceIls = locale.priceIls;

      if (!originalPrice || !originalCurrency) {
        return locale; // Skip if no price or currency info
      }

      try {
        // Convert the price to target currency
        const convertedPrice = convertCurrency(originalPrice, originalCurrency, targetCurrency);

        // Keep ILS price unchanged
        const convertedPriceIls = originalPriceIls;

        return {
          ...locale,
          price: convertedPrice,
          priceIls: convertedPriceIls,
          currency: targetCurrency,
          displayAmount: `${getCurrencySymbol(targetCurrency)}${convertedPrice.toFixed(2)}`,
          // Keep original values for reference
          originalPrice,
          originalCurrency,
          originalPriceIls,
        };
      } catch (error) {
        console.error('Currency conversion error:', error);
        return locale; // Return original if conversion fails
      }
    });
  };

  // Convert items with currency conversion
  const convertItemsPrices = (items, targetCurrency) => {
    if (!items || targetCurrency === 'local') {
      return items;
    }

    return items.map((item) => ({
      ...item,
      locales: convertLocalesPrices(item.locales, targetCurrency),
    }));
  };

  // Memoize the processed items to avoid unnecessary re-renders
  const processedItems = useMemo(() => {
    const baseItems = items || [];
    const allItems = [...baseItems, ...historicItems];
    if (allItems.length === 0) return [];
    const convertedItems = convertItemsPrices(allItems, selectedCurrency);
    return applySorting(convertedItems, sortOption);
  }, [items, historicItems, sortOption, selectedCurrency, currencyRates]);

  // Fetch historic items if initial search returned no results
  useEffect(() => {
    if (items && items.length === 0 && searchTerm && !historicSearched && featureFlags?.historicSearchFallback) {
      const fetchHistoricSearch = async () => {
        try {
          setHistoricSearched(true);
          const response = await fetch(`/api/historic-search?q=${encodeURIComponent(searchTerm)}`);
          if (response.ok) {
            const data = await response.json();
            if (data && data.searchResultsItems && Array.isArray(data.searchResultsItems)) {
              const processed = data.searchResultsItems.map(processSearchResultItemData).filter(Boolean);
              setHistoricItems(processed);
            }
          }
        } catch (error) {
          console.error('Failed to fetch historic search:', error);
        }
      };
      fetchHistoricSearch();
    }
  }, [items, searchTerm, historicSearched, featureFlags]);

  // Update local state when processed items change
  useEffect(() => {
    setItemsState(processedItems);
  }, [processedItems]);

  const mergeItems = (currentItems, newItems) => {
    const mergedItems = [...currentItems];
    let lastInsertedIndex = 0;
    newItems.forEach((newItem) => {
      const existingItemIndex = mergedItems.findIndex((item) => item.id === newItem.id);
      if (existingItemIndex > -1) {
        mergedItems[existingItemIndex].locales = [
          ...mergedItems[existingItemIndex].locales,
          ...newItem.locales.filter(
            (newLocale) =>
              !mergedItems[existingItemIndex].locales.some(
                (existingLocale) => existingLocale.locale === newLocale.locale
              )
          ),
        ];
        lastInsertedIndex = existingItemIndex;
      } else {
        mergedItems.splice(lastInsertedIndex + 1, 0, newItem);
        lastInsertedIndex++;
      }
    });
    return mergedItems;
  };

  const handleSearchVariants = async (asin) => {
    StatsWrapper.track('search.variants_clicked');

    try {
      const response = await fetch(`/api/variations?id=${asin}`);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const variationsData = await response.json();
      if (!variationsData || !Array.isArray(variationsData)) {
        console.error('Invalid variations data received, data:', variationsData);
        return;
      }
      setItemsState((prevItems) => [...mergeItems(prevItems, variationsData.map(formatItem))]);
    } catch (error) {
      console.error('Error fetching variations:', error);
      toast.error('Failed to fetch variations');
    }
  };

  // Check if filters are active
  const filtersActive = hasActiveFilters(activeFilters);
  const activeFilterCount = getActiveFilterCount(activeFilters);
  const currentItemsCount = itemsState?.length || 0;

  // Memoize missing domains calculation - based on ORIGINAL items to keep banners consistent
  const missingDomains = useMemo(() => getMissingDomains(items), [items]);


  /**
   * Calculate unique domains in the original search results
   * This represents the "health" of the Amazon API access
   */
  const successfulDomainsCount = useMemo(() => {
    if (!items || !Array.isArray(items)) return 0;
    const domains = new Set();
    items.forEach((item) => {
      if (item.locales && Array.isArray(item.locales)) {
        item.locales.forEach((locale) => {
          if (locale.domain) domains.add(locale.domain);
        });
      }
    });
    return domains.size;
  }, [items]);

  return (
    <div className="md:p-2">
      {/* A <div> can't legally nest inside a <p> - harmless under plain client rendering
          (just a console warning), but under SSR hydration the browser's HTML parser
          auto-closes the <p> early while parsing the static string, so the DOM it builds
          doesn't match what React expects to hydrate against (React #418/#423). */}
      <div className="text-sm text-gray-600">
        <div>** הקישורים המופיעים בתוצאות נוצרו באמצעות amazon affiliate</div>
      </div>

      {/* Missing domains banners - Community banner moved into grid for responsive row placement.
          Resolved by preloadHydratedChunks() before `hydrated` can be true - see
          hydrationPreload.js. */}
      {hydrated && MissingDomainsBanner && (
        <MissingDomainsBanner
          missingDomains={missingDomains}
          searchTerm={searchTerm}
          successfulDomainsCount={successfulDomainsCount}
        />
      )}

      {/* Filter indicators */}
      {filtersActive && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg" dir="rtl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-blue-600" />
              <span className="text-sm text-blue-800 font-medium">
                מוצגים {currentItemsCount} מתוך {originalItemsCount} תוצאות
              </span>
              <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                {activeFilterCount} סינונים פעילים
              </span>
            </div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:p-4">
        {/* TrackItemModal is mounted only once the user actually requests it (clicks
            "הרשמה למעקב"), not the instant hydration completes - see the same reasoning
            in Header.jsx for AuthModal. A search page can have dozens of other
            hydration-gated components (one ItemFullscreenModal per product card, plus
            every other modal/banner) all trying to mount in the same commit, which is
            what corrupted React's hooks dispatcher (#321/#418/#423 cascade). Its chunk
            is still preloaded eagerly (hydrationPreload.js) so there's no load delay. */}
        {trackModalRequest && TrackItemModal && (
          <TrackItemModal featureFlags={featureFlags} openRequest={trackModalRequest} />
        )}
        {/* Single shared instance for the whole page, mounted only while a product's
            fullscreen view is actually open - not one pre-mounted instance per card.
            `key` includes `token` (not just the asin) so reopening the same product
            during its close animation still forces a fresh mount instead of reusing
            the same fiber. See ItemFullscreenModal.jsx. */}
        {fullscreenItem && ItemFullscreenModal && (
          <ItemFullscreenModal
            key={`${fullscreenRequest.asin}-${fullscreenRequest.token}`}
            title={fullscreenItem.title}
            fullTitle={fullscreenItem.fullTitle}
            primaryImage={fullscreenItem.primaryImage}
            locales={fullscreenItem.locales}
            asin={fullscreenItem.id}
            dealAnalysis={fullscreenItem.dealAnalysis}
            description={fullscreenItem.description}
            freeShippingToIsrael={fullscreenItem.freeShippingToIsrael}
            shippingCostToIsrael={fullscreenItem.shippingCostToIsrael}
            finalPriceIls={fullscreenItem.finalPriceIls}
            featureFlags={featureFlags}
            isTracking={fullscreenItem.isTracking}
            onClose={() => setFullscreenRequest(null)}
          />
        )}
        {/* Mounted off `showVatInfo`, not `hydrated` directly - see where that state is
            declared above. */}
        {showVatInfo && VatInfoModal && <VatInfoModal />}
        {/* Resolved by preloadHydratedChunks() before `hydrated` can be true - see
            hydrationPreload.js. */}
        {hydrated && (
          <>
            {items?.length === 0 && AmazonCommunityBanner && (
              <AmazonCommunityBanner
                missingDomains={missingDomains}
                searchTerm={searchTerm}
                successfulDomainsCount={successfulDomainsCount}
                urlAsin={urlAsin}
                className="col-span-full my-8"
              />
            )}
          </>
        )}

        {itemsState?.length > 0 && itemsState.map((item, index) => {
              const {
                id,
                title,
                primaryImage,
                locales,
                dealAnalysis,
                fullTitle,
                freeShippingToIsrael,
                shippingCostToIsrael,
                isTracking,
              } = item;

              const communityBannerProps = {
                missingDomains,
                searchTerm,
                successfulDomainsCount,
                urlAsin,
              };

              const renderProductCard = () => (
                <ProductCard
                  key={id || `card-${index}`}
                  title={title}
                  fullTitle={fullTitle}
                  primaryImage={primaryImage}
                  locales={locales}
                  asin={id}
                  onSearchVariants={handleSearchVariants}
                  dealAnalysis={dealAnalysis}
                  freeShippingToIsrael={freeShippingToIsrael}
                  shippingCostToIsrael={shippingCostToIsrael}
                  isTracking={isTracking}
                />
              );

              return (
                <React.Fragment key={id || `result-${index}`}>
                  {index === aliExpressAdIndex && aliExpressProduct && hydrated && AliExpressAdCard && (
                    <AliExpressAdCard
                      key="aliexpress-ad"
                      product={aliExpressProduct}
                      searchTerm={searchTerm}
                    />
                  )}
                  {index === newAmazonInviteIndex ? (
                    <>
                      <NewAmazonCard
                        orderCount={2}
                        key="new-amazon"
                        amazonLocation="יפן"
                        image={japaneseFlag}
                        referralLink="https://www.amazon.co.jp?&linkCode=ll2&tag=botimza-22&linkId=1a200153da918ee9b06f339f909a98e5&language=en_US&ref_=as_li_ss_tl"
                      />
                      {renderProductCard()}
                    </>
                  ) : (
                    <>{renderProductCard()}</>
                  )}

                  {/* Community Banner rendered after the first row of results for each breakpoint */}
                  {index === 0 && hydrated && AmazonCommunityBanner && (
                    <AmazonCommunityBanner
                      {...communityBannerProps}
                      className="col-span-full block md:hidden mb-4"
                    />
                  )}
                  {index === Math.min(itemsState.length - 1, 1) && hydrated && AmazonCommunityBanner && (
                    <AmazonCommunityBanner
                      {...communityBannerProps}
                      className="col-span-full hidden md:block lg:hidden mb-4"
                    />
                  )}
                  {index === Math.min(itemsState.length - 1, 3) && hydrated && AmazonCommunityBanner && (
                    <AmazonCommunityBanner
                      {...communityBannerProps}
                      className="col-span-full hidden lg:block mb-4"
                    />
                  )}
                </React.Fragment>
              );
            })}
      </div>
    </div>
  );
}

export default SearchResults;
